import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  message,
  Table,
  Tag,
  Space,
  Modal,
  Input,
  Select,
  Form,
  Descriptions,
  Statistic,
  Row,
  Col,
  Tooltip,
  Typography
} from 'antd';
import {
  KeyOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  UserOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  GlobalOutlined,
  PlusOutlined,
  CloseOutlined
} from '@ant-design/icons';
import API from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const { Option } = Select;
const { Text } = Typography;

const AdminAPIKeyManagement = () => {
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editQpsModalVisible, setEditQpsModalVisible] = useState(false);
  const [editDailyLimitModalVisible, setEditDailyLimitModalVisible] = useState(false);
  const [editSessionLimitModalVisible, setEditSessionLimitModalVisible] = useState(false);
  const [ipWhitelistModalVisible, setIpWhitelistModalVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  
  const [qpsForm] = Form.useForm();
  const [dailyLimitForm] = Form.useForm();
  const [sessionLimitForm] = Form.useForm();
  
  // IP白名单相关状态
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [ipWhitelistLoading, setIpWhitelistLoading] = useState(false);

  // 获取API密钥列表
  const fetchAPIKeys = async (page = pagination.current, limit = pagination.pageSize, status = statusFilter) => {
    setLoading(true);
    try {
      const response = await API.get('/api/admin/api-keys', {
        params: {
          page,
          limit,
          status
        }
      });

      if (response.data.success) {
        setApiKeys(response.data.data.list);
        setPagination({
          current: response.data.data.page,
          pageSize: response.data.data.limit,
          total: response.data.data.total
        });
      } else {
        message.error(response.data.message || '获取API密钥列表失败');
      }
    } catch (error) {
      console.error('获取API密钥列表错误:', error);
      message.error(error.response?.data?.message || '获取API密钥列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取密钥详情
  const fetchKeyDetail = async (keyId) => {
    try {
      const response = await API.get(`/api/admin/api-keys/${keyId}`);

      if (response.data.success) {
        setSelectedKey(response.data.data);
        setDetailModalVisible(true);
      } else {
        message.error(response.data.message || '获取密钥详情失败');
      }
    } catch (error) {
      console.error('获取密钥详情错误:', error);
      message.error(error.response?.data?.message || '获取密钥详情失败');
    }
  };

  // 设置QPS限制
  const updateQpsLimit = async (values) => {
    try {
      const response = await API.put(`/api/admin/api-keys/${selectedKey._id}/qps`, {
        qpsLimit: values.qpsLimit
      });

      if (response.data.success) {
        message.success('QPS限制已更新');
        setEditQpsModalVisible(false);
        qpsForm.resetFields();
        fetchAPIKeys();
      } else {
        message.error(response.data.message || '更新失败');
      }
    } catch (error) {
      console.error('更新QPS限制错误:', error);
      message.error(error.response?.data?.message || '更新失败');
    }
  };

  // 设置每日限制
  const updateDailyLimit = async (values) => {
    try {
      const response = await API.put(`/api/admin/api-keys/${selectedKey._id}/daily-limit`, {
        dailyLimit: values.dailyLimit
      });

      if (response.data.success) {
        message.success('每日限制已更新');
        setEditDailyLimitModalVisible(false);
        dailyLimitForm.resetFields();
        fetchAPIKeys();
      } else {
        message.error(response.data.message || '更新失败');
      }
    } catch (error) {
      console.error('更新每日限制错误:', error);
      message.error(error.response?.data?.message || '更新失败');
    }
  };

  // 设置会话限制
  const updateSessionLimit = async (values) => {
    try {
      const response = await API.put(`/api/admin/api-keys/${selectedKey._id}/session-limit`, {
        sessionLimit: values.sessionLimit
      });

      if (response.data.success) {
        message.success('会话限制已更新');
        setEditSessionLimitModalVisible(false);
        sessionLimitForm.resetFields();
        fetchAPIKeys();
      } else {
        message.error(response.data.message || '更新失败');
      }
    } catch (error) {
      console.error('更新会话限制错误:', error);
      message.error(error.response?.data?.message || '更新失败');
    }
  };

  // 启用/禁用密钥
  const toggleKeyStatus = async (keyId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    const actionText = newStatus === 'active' ? '启用' : '禁用';

    Modal.confirm({
      title: `确认${actionText}密钥`,
      content: `确定要${actionText}该API密钥吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await API.put(`/api/admin/api-keys/${keyId}/status`, {
            status: newStatus
          });

          if (response.data.success) {
            message.success(`密钥已${actionText}`);
            fetchAPIKeys();
          } else {
            message.error(response.data.message || `${actionText}失败`);
          }
        } catch (error) {
          console.error(`${actionText}密钥错误:`, error);
          message.error(error.response?.data?.message || `${actionText}失败`);
        }
      }
    });
  };

  // 删除API密钥
  const deleteAPIKey = (keyId, userName) => {
    Modal.confirm({
      title: '确认删除API密钥',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定要删除用户 <strong>{userName}</strong> 的API密钥吗？</p>
          <p style={{ color: '#ff4d4f', fontWeight: 500 }}>
            此操作不可恢复！删除后该用户将无法使用此密钥调用API。
          </p>
        </div>
      ),
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await API.delete(`/api/admin/api-keys/${keyId}`);

          if (response.data.success) {
            message.success(response.data.message || 'API密钥已删除');
            fetchAPIKeys();
          } else {
            message.error(response.data.message || '删除失败');
          }
        } catch (error) {
          console.error('删除API密钥错误:', error);
          message.error(error.response?.data?.message || '删除失败');
        }
      }
    });
  };

  // 打开编辑QPS Modal
  const openEditQpsModal = (record) => {
    setSelectedKey(record);
    qpsForm.setFieldsValue({
      qpsLimit: record.qpsLimit
    });
    setEditQpsModalVisible(true);
  };

  // 打开编辑每日限制 Modal
  const openEditDailyLimitModal = (record) => {
    setSelectedKey(record);
    dailyLimitForm.setFieldsValue({
      dailyLimit: record.dailyLimit
    });
    setEditDailyLimitModalVisible(true);
  };

  // 打开编辑会话限制 Modal
  const openEditSessionLimitModal = (record) => {
    setSelectedKey(record);
    sessionLimitForm.setFieldsValue({
      sessionLimit: record.sessionLimit
    });
    setEditSessionLimitModalVisible(true);
  };

  // 打开IP白名单管理 Modal
  const openIpWhitelistModal = (record) => {
    setSelectedKey(record);
    setIpWhitelist(record.ipWhitelist || []);
    setIpWhitelistModalVisible(true);
  };

  // 添加IP到白名单
  const handleAddIp = () => {
    if (!newIp.trim()) {
      message.warning('请输入IP地址');
      return;
    }

    if (ipWhitelist.includes(newIp.trim())) {
      message.warning('IP已在白名单中');
      return;
    }

    setIpWhitelist([...ipWhitelist, newIp.trim()]);
    setNewIp('');
  };

  // 从白名单移除IP
  const handleRemoveIp = (ip) => {
    setIpWhitelist(ipWhitelist.filter(item => item !== ip));
  };

  // 保存IP白名单
  const handleSaveIpWhitelist = async () => {
    setIpWhitelistLoading(true);
    try {
      const response = await API.put(`/api/admin/api-keys/${selectedKey._id}/ip-whitelist`, {
        ipWhitelist
      });

      if (response.data.success) {
        message.success('IP白名单已更新');
        setIpWhitelistModalVisible(false);
        setNewIp('');
        fetchAPIKeys();
      } else {
        message.error(response.data.message || '更新失败');
      }
    } catch (error) {
      console.error('更新IP白名单错误:', error);
      message.error(error.response?.data?.message || '更新失败');
    } finally {
      setIpWhitelistLoading(false);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '用户信息',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div>
            <UserOutlined /> {record.user.username}
          </div>
          <div style={{ fontSize: 12, color: '#999' }}>
            {record.user.email}
          </div>
          <Tag color={
            record.user.role === 'admin' ? 'red' :
            record.user.role === 'wlj-user' ? 'green' : 'blue'
          }>
            {record.user.role === 'admin' ? '管理员' :
             record.user.role === 'wlj-user' ? 'WLJ用户' : '用户'}
          </Tag>
        </Space>
      )
    },
    {
      title: '密钥名称',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '激活' : '已禁用'}
        </Tag>
      )
    },
    {
      title: 'QPS限制',
      dataIndex: 'qpsLimit',
      key: 'qpsLimit',
      width: 120,
      render: (qpsLimit, record) => (
        <Tooltip title="点击编辑">
          <Button
            type="link"
            size="small"
            onClick={() => openEditQpsModal(record)}
          >
            {qpsLimit} 次/秒
          </Button>
        </Tooltip>
      )
    },
    {
      title: '每日限制',
      dataIndex: 'dailyLimit',
      key: 'dailyLimit',
      width: 120,
      render: (dailyLimit, record) => (
        <Tooltip title="点击编辑">
          <Button
            type="link"
            size="small"
            onClick={() => openEditDailyLimitModal(record)}
          >
            {dailyLimit} 次/天
          </Button>
        </Tooltip>
      )
    },
    {
      title: '今日使用',
      dataIndex: 'todayUsage',
      key: 'todayUsage',
      width: 120,
      render: (todayUsage, record) => {
        const percentage = (todayUsage / record.dailyLimit * 100).toFixed(1);
        const color = percentage >= 90 ? '#cf1322' : percentage >= 70 ? '#faad14' : '#52c41a';
        return (
          <span style={{ color }}>
            {todayUsage} ({percentage}%)
          </span>
        );
      }
    },
    {
      title: '会话限制',
      dataIndex: 'sessionLimit',
      key: 'sessionLimit',
      width: 120,
      render: (sessionLimit, record) => (
        <Tooltip title="点击编辑">
          <Button
            type="link"
            size="small"
            onClick={() => openEditSessionLimitModal(record)}
          >
            {sessionLimit === 0 ? '不限制' : `${sessionLimit} 个`}
          </Button>
        </Tooltip>
      )
    },
    {
      title: '会话数量',
      dataIndex: 'sessionCount',
      key: 'sessionCount',
      width: 100,
      render: (sessionCount, record) => {
        const isOverLimit = record.sessionLimit > 0 && sessionCount >= record.sessionLimit;
        return (
          <span style={{ color: isOverLimit ? '#cf1322' : '#52c41a' }}>
            {sessionCount || 0}
            {isOverLimit && ' ⚠️'}
          </span>
        );
      }
    },
    {
      title: '总使用',
      dataIndex: 'totalUsage',
      key: 'totalUsage',
      width: 100
    },
    {
      title: '最后使用',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      width: 150,
      render: (lastUsedAt) => lastUsedAt ? (
        <Tooltip title={dayjs(lastUsedAt).format('YYYY-MM-DD HH:mm:ss')}>
          {dayjs(lastUsedAt).fromNow()}
        </Tooltip>
      ) : (
        <span style={{ color: '#999' }}>从未使用</span>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (createdAt) => (
        <Tooltip title={dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss')}>
          {dayjs(createdAt).fromNow()}
        </Tooltip>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => fetchKeyDetail(record._id)}
            size="small"
          >
            详情
          </Button>
          <Button
            type="link"
            icon={<GlobalOutlined />}
            onClick={() => openIpWhitelistModal(record)}
            size="small"
          >
            白名单
          </Button>
          <Button
            type="link"
            danger={record.status === 'active'}
            icon={record.status === 'active' ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => toggleKeyStatus(record._id, record.status)}
            size="small"
          >
            {record.status === 'active' ? '禁用' : '启用'}
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteAPIKey(record._id, record.user.username)}
            size="small"
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  // 表格分页变化处理
  const handleTableChange = (newPagination) => {
    fetchAPIKeys(newPagination.current, newPagination.pageSize, statusFilter);
  };

  // 状态筛选变化
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchAPIKeys(1, pagination.pageSize, value);
  };

  useEffect(() => {
    fetchAPIKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: 24, maxWidth: 1600, margin: '0 auto' }}>
      <Card
        title={
          <Space>
            <KeyOutlined style={{ fontSize: 20 }} />
            <span>API 密钥管理</span>
          </Space>
        }
        extra={
          <Space>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              style={{ width: 120 }}
            >
              <Option value="all">全部状态</Option>
              <Option value="active">激活</Option>
              <Option value="disabled">已禁用</Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchAPIKeys()}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={apiKeys}
          rowKey="_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>

      {/* 密钥详情 Modal */}
      <Modal
        title="API密钥详情"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedKey(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedKey && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="今日使用"
                    value={selectedKey.todayUsage || 0}
                    suffix={`/ ${selectedKey.dailyLimit}`}
                    valueStyle={{ 
                      color: selectedKey.todayUsage >= selectedKey.dailyLimit ? '#cf1322' : '#3f8600' 
                    }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="总调用"
                    value={selectedKey.totalUsage || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="QPS限制"
                    value={selectedKey.qpsLimit}
                    suffix="次/秒"
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="每日限制"
                    value={selectedKey.dailyLimit}
                    suffix="次/天"
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Card>
                  <Statistic
                    title="会话限制"
                    value={selectedKey.sessionLimit === 0 ? '不限制' : selectedKey.sessionLimit}
                    suffix={selectedKey.sessionLimit === 0 ? '' : '个'}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card>
                  <Statistic
                    title="会话数量"
                    value={selectedKey.sessionCount || 0}
                    suffix={selectedKey.sessionLimit > 0 ? `/ ${selectedKey.sessionLimit}` : ''}
                    valueStyle={{ 
                      color: selectedKey.sessionLimit > 0 && selectedKey.sessionCount >= selectedKey.sessionLimit 
                        ? '#cf1322' 
                        : '#3f8600' 
                    }}
                  />
                </Card>
              </Col>
            </Row>

            <Descriptions column={2} bordered>
              <Descriptions.Item label="用户名">
                {selectedKey.user.username}
              </Descriptions.Item>
              <Descriptions.Item label="用户邮箱">
                {selectedKey.user.email}
              </Descriptions.Item>
              <Descriptions.Item label="用户角色">
                <Tag color={
                  selectedKey.user.role === 'admin' ? 'red' :
                  selectedKey.user.role === 'wlj-user' ? 'green' : 'blue'
                }>
                  {selectedKey.user.role === 'admin' ? '管理员' :
                   selectedKey.user.role === 'wlj-user' ? 'WLJ用户' : '用户'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="密钥状态">
                <Tag color={selectedKey.status === 'active' ? 'success' : 'default'}>
                  {selectedKey.status === 'active' ? '激活' : '已禁用'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="密钥名称" span={2}>
                {selectedKey.name}
              </Descriptions.Item>
              <Descriptions.Item label="API Key" span={2}>
                <Input.Password
                  value={selectedKey.key}
                  readOnly
                  style={{
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: 13
                  }}
                />
              </Descriptions.Item>
              <Descriptions.Item label="会话限制">
                {selectedKey.sessionLimit === 0 ? '不限制' : `${selectedKey.sessionLimit} 个`}
              </Descriptions.Item>
              <Descriptions.Item label="会话数量">
                <span style={{ 
                  color: selectedKey.sessionLimit > 0 && selectedKey.sessionCount >= selectedKey.sessionLimit 
                    ? '#cf1322' 
                    : 'inherit' 
                }}>
                  {selectedKey.sessionCount || 0}
                  {selectedKey.sessionLimit > 0 && selectedKey.sessionCount >= selectedKey.sessionLimit && ' ⚠️ 已达上限'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="IP白名单" span={2}>
                {selectedKey.ipWhitelist && selectedKey.ipWhitelist.length > 0 ? (
                  <Space wrap>
                    {selectedKey.ipWhitelist.map((ip, index) => (
                      <Tag key={index} color="blue" style={{ fontFamily: 'Monaco, Consolas, monospace' }}>
                        {ip}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Tag color="default">未设置（所有IP可访问）</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(selectedKey.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="最后使用">
                {selectedKey.lastUsedAt ? 
                  dayjs(selectedKey.lastUsedAt).format('YYYY-MM-DD HH:mm:ss') : 
                  '从未使用'
                }
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>

      {/* 编辑QPS限制 Modal */}
      <Modal
        title={
          <Space>
            <ThunderboltOutlined />
            <span>设置QPS限制</span>
          </Space>
        }
        open={editQpsModalVisible}
        onCancel={() => {
          setEditQpsModalVisible(false);
          qpsForm.resetFields();
        }}
        onOk={() => qpsForm.submit()}
        okText="保存"
        cancelText="取消"
      >
        {selectedKey && (
          <>
            <p style={{ marginBottom: 16, color: '#666' }}>
              用户：<strong>{selectedKey.user.username}</strong>（{selectedKey.user.email}）
            </p>
            <Form
              form={qpsForm}
              onFinish={updateQpsLimit}
              layout="vertical"
            >
              <Form.Item
                label="QPS限制（每秒请求数）"
                name="qpsLimit"
                rules={[
                  { required: true, message: '请输入QPS限制' },
                  { 
                    validator: (_, value) => {
                      const num = Number(value);
                      if (isNaN(num) || num < 1) {
                        return Promise.reject(new Error('QPS限制必须大于0'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  type="number"
                  suffix="次/秒"
                  placeholder="请输入QPS限制"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* 编辑每日限制 Modal */}
      <Modal
        title={
          <Space>
            <CalendarOutlined />
            <span>设置每日限制</span>
          </Space>
        }
        open={editDailyLimitModalVisible}
        onCancel={() => {
          setEditDailyLimitModalVisible(false);
          dailyLimitForm.resetFields();
        }}
        onOk={() => dailyLimitForm.submit()}
        okText="保存"
        cancelText="取消"
      >
        {selectedKey && (
          <>
            <p style={{ marginBottom: 16, color: '#666' }}>
              用户：<strong>{selectedKey.user.username}</strong>（{selectedKey.user.email}）
            </p>
            <Form
              form={dailyLimitForm}
              onFinish={updateDailyLimit}
              layout="vertical"
            >
              <Form.Item
                label="每日限制（每天请求数）"
                name="dailyLimit"
                rules={[
                  { required: true, message: '请输入每日限制' },
                  { 
                    validator: (_, value) => {
                      const num = Number(value);
                      if (isNaN(num) || num < 1) {
                        return Promise.reject(new Error('每日限制必须大于0'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  type="number"
                  suffix="次/天"
                  placeholder="请输入每日限制"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* 编辑会话限制 Modal */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            <span>设置会话限制</span>
          </Space>
        }
        open={editSessionLimitModalVisible}
        onCancel={() => {
          setEditSessionLimitModalVisible(false);
          sessionLimitForm.resetFields();
        }}
        onOk={() => sessionLimitForm.submit()}
        okText="保存"
        cancelText="取消"
      >
        {selectedKey && (
          <>
            <p style={{ marginBottom: 16, color: '#666' }}>
              用户：<strong>{selectedKey.user.username}</strong>（{selectedKey.user.email}）
            </p>
            <Form
              form={sessionLimitForm}
              onFinish={updateSessionLimit}
              layout="vertical"
            >
              <Form.Item
                label="会话限制（允许的会话数量）"
                name="sessionLimit"
                rules={[
                  { required: true, message: '请输入会话限制' },
                  { 
                    validator: (_, value) => {
                      const num = Number(value);
                      if (isNaN(num) || num < 0) {
                        return Promise.reject(new Error('会话限制必须大于等于0'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
                extra="设置为 0 表示不限制会话数量"
              >
                <Input
                  type="number"
                  suffix="个"
                  placeholder="请输入会话限制（0表示不限制）"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* IP白名单管理 Modal */}
      <Modal
        title={
          <Space>
            <GlobalOutlined />
            <span>IP 白名单管理</span>
          </Space>
        }
        open={ipWhitelistModalVisible}
        onCancel={() => {
          setIpWhitelistModalVisible(false);
          setNewIp('');
        }}
        onOk={handleSaveIpWhitelist}
        confirmLoading={ipWhitelistLoading}
        okText="保存"
        cancelText="取消"
        width={700}
      >
        {selectedKey && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* 用户信息 */}
            <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
              <Text strong>用户：</Text>
              <Text>{selectedKey.user.username}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>（{selectedKey.user.email}）</Text>
            </div>

            {/* 说明 */}
            <Card size="small" style={{ background: '#e6f7ff', border: '1px solid #91d5ff' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text strong style={{ color: '#1890ff' }}>
                  <GlobalOutlined style={{ marginRight: 4 }} />
                  IP白名单说明
                </Text>
                <Text style={{ fontSize: 12 }}>
                  配置IP白名单后，只有白名单中的IP地址才能使用此API密钥。
                </Text>
                <Text style={{ fontSize: 12 }}>
                  <strong>支持格式：</strong>单个IP（192.168.1.1）、通配符（192.168.1.*）、CIDR（192.168.1.0/24）
                </Text>
              </Space>
            </Card>

            {/* 添加IP */}
            <Card size="small" title="添加IP地址">
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  placeholder="输入IP地址（支持通配符和CIDR）"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  onPressEnter={handleAddIp}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddIp}
                >
                  添加
                </Button>
              </Space.Compact>
            </Card>

            {/* IP列表 */}
            <Card 
              size="small" 
              title={
                <span>
                  白名单列表 
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {ipWhitelist.length} 个IP
                  </Tag>
                </span>
              }
            >
              {ipWhitelist.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
                  <GlobalOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                  <div>暂无IP白名单，所有IP均可访问</div>
                </div>
              ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {ipWhitelist.map((ip, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        background: '#fafafa',
                        borderRadius: 4,
                        border: '1px solid #f0f0f0'
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: 'Monaco, Consolas, monospace',
                          fontSize: 13
                        }}
                      >
                        {ip}
                      </Text>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => handleRemoveIp(ip)}
                      >
                        移除
                      </Button>
                    </div>
                  ))}
                </Space>
              )}
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default AdminAPIKeyManagement;

